import http from './api'

export interface GameInvitation {
  _id: string
  id?: string
  room: string | {
    _id: string
    name: string
  }
  challenger: string | {
    _id: string
    name: string
    email?: string
  }
  opponent: string | {
    _id: string
    name: string
    email?: string
  }
  status: 'pending' | 'accepted' | 'rejected'
  gameId?: string
  createdAt: string
  updatedAt: string
}

class GameInvitationService {
  async createInvitation(data: {
    roomId: string
    opponentId: string
  }): Promise<GameInvitation> {
    // Try chess-game-invitations first, fallback to game-invitations
    try {
      const response = await http.post('/chess-game-invitations', {
        roomId: data.roomId,
        opponentId: data.opponentId,
      })
      return response.data?.invitation || response.data
    } catch (error: any) {
      // Fallback to game-invitations endpoint
      if (error.response?.status === 404) {
        try {
          const response = await http.post('/game-invitations', {
            roomId: data.roomId,
            opponentId: data.opponentId,
          })
          return response.data?.invitation || response.data
        } catch (fallbackError: any) {
          // Re-throw the original error if fallback also fails
          throw error
        }
      }
      throw error
    }
  }

  async getUserInvitations(): Promise<GameInvitation[]> {
    // Try chess-game-invitations first, fallback to game-invitations
    // Use validateStatus to prevent 404s from being logged as errors
    try {
      const response = await http.get('/chess-game-invitations', {
        validateStatus: (status) => status === 200 || status === 404
      })
      
      if (response.status === 404) {
        // Try fallback endpoint
        try {
          const fallbackResponse = await http.get('/game-invitations', {
            validateStatus: (status) => status === 200 || status === 404
          })
          
          if (fallbackResponse.status === 404) {
            return []
          }
          
          const invitations = fallbackResponse.data?.invitations || fallbackResponse.data
          return Array.isArray(invitations) ? invitations : []
        } catch (fallbackError: any) {
          // If fallback also fails, return empty array silently
          return []
        }
      }
      
      const invitations = response.data?.invitations || response.data
      return Array.isArray(invitations) ? invitations : []
    } catch (error: any) {
      // Return empty array for any other error
      return []
    }
  }

  async updateInvitation(invitationId: string, status: 'accepted' | 'rejected'): Promise<any> {
    // Try chess-game-invitations first, fallback to game-invitations
    try {
      const response = await http.patch(`/chess-game-invitations/${invitationId}`, {
        status,
      })
      return response.data
    } catch (error: any) {
      // Fallback to game-invitations endpoint
      if (error.response?.status === 404) {
        try {
          const response = await http.patch(`/game-invitations/${invitationId}`, {
            status,
          })
          return response.data
        } catch (fallbackError: any) {
          // Re-throw the original error if fallback also fails
          throw error
        }
      }
      throw error
    }
  }

  async markInvitationsAsRead(): Promise<void> {
    // Try chess-game-invitations first, fallback to game-invitations
    // Use validateStatus to prevent 404s from being logged as errors
    try {
      const response = await http.get('/chess-game-invitations/read-all', {
        validateStatus: (status) => status === 200 || status === 404
      })
      
      if (response.status === 404) {
        // Try fallback endpoint
        try {
          await http.get('/game-invitations/read-all', {
            validateStatus: (status) => status === 200 || status === 404
          })
        } catch (fallbackError: any) {
          // Silently fail if both endpoints don't exist
          return
        }
      }
    } catch (error: any) {
      // Silently fail for any other error
    }
  }
}

const gameInvitationService = new GameInvitationService()
export default gameInvitationService

